import { useCallback } from 'react';

import { useToasts } from '@air/provider-toast';

import { usePrivateAccountDeletePendingEmailMutation } from '~/components/v2/hooks/queries/mutations/usePrivateAccountDeletePendingEmailMutation';
import { usePendingEmailFocusRefetch } from '~/hooks/usePendingEmailFocusRefetch';
import { usePendingEmailResend } from '~/hooks/usePendingEmailResend';
import { useAccountContext } from '~/providers/AccountProvider';
import { getResendAriaLabel } from '~/utils/getResendAriaLabel';

/**
 * Shared derived state and handlers for the pending-email UI surfaces
 * (banner + notice). Keeps the resend / cancel / focus-refetch wiring in
 * one place so the two surfaces can't drift on aria labels, disabled
 * gating, or when the account query gets invalidated on tab focus.
 */
export const usePendingEmailSurface = () => {
  const { data: account } = useAccountContext();
  const { showToast } = useToasts();
  const { isResending, resend, secondsRemaining } = usePendingEmailResend();
  const { mutate: deletePendingEmail, isPending: isCanceling } = usePrivateAccountDeletePendingEmailMutation();

  usePendingEmailFocusRefetch();

  const cancel = useCallback(() => {
    deletePendingEmail(undefined, {
      onError: () => {
        showToast('Failed to cancel email change', { color: 'red' });
      },
    });
  }, [deletePendingEmail, showToast]);

  const isCoolingDown = secondsRemaining > 0;
  const isResendDisabled = isResending || isCoolingDown;
  const resendAriaLabel = getResendAriaLabel({ isResending, isCoolingDown });

  return {
    cancel,
    isCanceling,
    isCoolingDown,
    isResendDisabled,
    isResending,
    pendingEmail: account?.pendingEmail,
    resend,
    resendAriaLabel,
    secondsRemaining,
  };
};
